/**
 * Broad event topics and start-time buckets, shared by the public filter bar
 * and subscription matching.
 *
 * Keep api/src/lib/event-topic-data.ts and frontend/src/lib/eventTopicData.ts
 * identical, with no imports. Neither deployment can import the other package;
 * tests check parity.
 *
 * Each topic is a mapping over tags the extractor already emits, so no
 * re-extraction is needed. An event belongs to a topic when its eventType or
 * any tag is in the topic's list; tags may sit in more than one topic. Events
 * with no topic tag at all fall back to their venue's category.
 *
 * Time of day comes from the event's New York start clock, never from tags,
 * because tags can disagree with the stored time. All-day and "Time TBA"
 * events match no clock bucket. Brunch stays tag-based as a flavour.
 */

export interface EventTopic {
  id: string;
  label: string;
  icon: string;
  /** Tag / eventType slugs that put an event in this topic. */
  tags: string[];
  /** Venue categories used only when the event has no topic tag. */
  venueCategories: string[];
}

export const MUSIC_GENRE_TAGS = [
  'rock', 'indie-rock', 'alternative', 'punk', 'metal', 'jazz', 'blues', 'soul', 'r-and-b',
  'hip-hop', 'rap', 'electronic', 'edm', 'house', 'techno', 'folk', 'country', 'bluegrass',
  'reggae', 'latin', 'classical', 'singer-songwriter', 'cover-band', 'tribute-band', 'acoustic',
  'funk', 'gospel', 'world-music',
];

export const EVENT_TOPICS: EventTopic[] = [
  {
    id: 'music',
    label: 'Music',
    icon: '🎵',
    tags: [
      'music', 'concert', 'live-music', 'dj-set', 'open-mic', 'karaoke', 'jazz-night',
      'opera', 'orchestra', 'symphony', 'chamber-music', ...MUSIC_GENRE_TAGS,
    ],
    venueCategories: ['concert_hall', 'philharmonic_hall', 'opera_house', 'amphitheatre', 'karaoke'],
  },
  {
    id: 'stage',
    label: 'Comedy & stage',
    icon: '🎭',
    tags: [
      'comedy', 'stand-up', 'improv', 'drag-show', 'burlesque', 'theater', 'theatre',
      'theater-production', 'musical-theater', 'dance-performance', 'ballet', 'auditions',
    ],
    venueCategories: ['performing_arts_theater', 'auditorium'],
  },
  {
    id: 'art-film',
    label: 'Art & film',
    icon: '🎨',
    tags: [
      'art', 'film', 'art-show', 'gallery-opening', 'art-walk', 'art-exhibition', 'open-studios',
      'photography', 'mural', 'sculpture', 'film-screening', 'film-festival', 'indie-film',
      'film-society', 'short-films', 'documentary',
    ],
    venueCategories: ['art_gallery', 'art_studio', 'museum', 'movie_theater', 'sculpture'],
  },
  {
    id: 'talks',
    label: 'Talks & books',
    icon: '📚',
    tags: [
      'lecture', 'book-club', 'poetry', 'spoken-word', 'story-slam', 'author-talk', 'book-signing',
      'zine', 'writing-workshop', 'literary-festival', 'history-talk', 'historic-tour',
      'heritage-festival', 'architecture-tour',
    ],
    venueCategories: [
      'library', 'book_store', 'cultural_landmark', 'historical_place', 'historical_landmark',
      'monument', 'visitor_center', 'planetarium',
    ],
  },
  {
    id: 'classes',
    label: 'Classes & making',
    icon: '🛠️',
    tags: [
      'workshop', 'class', 'cooking-class', 'cocktail-class', 'dance-class', 'writing-workshop',
      'garden-workshop', 'pottery', 'ceramics', 'yarn-and-fiber', 'knitting', 'sewing', 'quilting',
      'printmaking', 'jewelry-making', 'woodworking', 'watercolor', 'drawing', 'collage',
      'bookbinding', 'leatherwork', 'glassblowing', 'repair-cafe', 'fix-it-clinic',
      'tool-library', 'makerspace',
    ],
    venueCategories: [],
  },
  {
    id: 'games',
    label: 'Games & trivia',
    icon: '🧠',
    tags: [
      'games', 'trivia', 'bingo', 'game-night', 'puzzle', 'puzzle-hunt', 'puzzle-swap', 'escape-room',
      'scavenger-hunt', 'crossword', 'jigsaw-puzzle', 'riddle-night', 'board-games',
      'tabletop-games', 'tcg-tournament', 'chess', 'esports', 'tabletop-rpg', 'game-store-event',
    ],
    venueCategories: [],
  },
  {
    id: 'nightlife',
    label: 'Dance & nightlife',
    icon: '💃',
    tags: [
      'dance', 'nightlife', 'dance-night', 'after-party', 'dance-class', 'social-dance', 'salsa',
      'swing-dance', 'tango', 'zouk', 'line-dancing',
    ],
    venueCategories: ['night_club', 'dance_hall'],
  },
  {
    id: 'food',
    label: 'Food & drink',
    icon: '🍽️',
    tags: [
      'food', 'food-festival', 'beer-garden', 'wine-tasting', 'cocktail-class', 'cooking-class',
      'brunch-event', 'happy-hour', 'food-truck', 'pop-up',
    ],
    venueCategories: ['bar', 'pub', 'wine_bar', 'restaurant', 'cafe'],
  },
  {
    id: 'markets',
    label: 'Markets',
    icon: '🛍️',
    tags: [
      'market', 'craft-fair', 'farmers-market', 'maker-market', 'artisan-market', 'handmade',
      'flea-market', 'antique-show', 'vintage-market', 'estate-sale', 'collectibles',
      'pop-up-market', 'plant-sale',
    ],
    venueCategories: [
      'market', 'farmers_market', 'flea_market', 'gift_shop', 'jewelry_store', 'garden_center', 'florist',
    ],
  },
  {
    id: 'outdoors',
    label: 'Outdoors & active',
    icon: '🌳',
    tags: [
      'sports', 'yoga', 'fitness', 'run', 'cycling', 'outdoor-activity', 'native-plants', 'plant-sale',
      'garden-tour', 'tree-planting', 'garden-workshop', 'seed-swap', 'composting', 'pollinator',
      'horticulture', 'nature-walk', 'birding', 'hiking', 'kayak', 'wildlife', 'conservation',
    ],
    venueCategories: [
      'park', 'national_park', 'state_park', 'hiking_area', 'botanical_garden', 'garden',
      'nature_preserve', 'wildlife_park', 'wildlife_refuge', 'farm', 'zoo', 'aquarium', 'yoga_studio',
    ],
  },
  {
    id: 'community',
    label: 'Community',
    icon: '🤝',
    tags: [
      'community', 'fundraiser', 'charity', 'networking', 'community-meeting', 'watch-party', 'parade',
      'festival', 'holiday-event', 'birthday-celebration', 'open-house', 'block-party', 'volunteer',
      'workday', 'cleanup', 'donation-drive', 'mutual-aid', 'nonprofit',
    ],
    venueCategories: ['community_center', 'cultural_center', 'event_venue', 'convention_center'],
  },
];

export const EVENT_TOPIC_IDS = new Set(EVENT_TOPICS.map((t) => t.id));

function indexBy(pick: (t: EventTopic) => string[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const topic of EVENT_TOPICS) {
    for (const key of pick(topic)) m.set(key, [...(m.get(key) ?? []), topic.id]);
  }
  return m;
}

const TOPICS_BY_TAG = indexBy((t) => t.tags);
const TOPICS_BY_VENUE_CATEGORY = indexBy((t) => t.venueCategories);

export interface TopicInput {
  eventType?: string | null;
  tags?: readonly string[] | null;
  /** The event's primary venue category, when known. */
  category?: string | null;
}

/** Topic ids for an event: tag and eventType matches, else its venue category. */
export function eventTopicIds(event: TopicInput): Set<string> {
  const out = new Set<string>();
  for (const s of [event.eventType, ...(event.tags ?? [])]) {
    const key = s?.trim().toLowerCase();
    if (!key) continue;
    for (const id of TOPICS_BY_TAG.get(key) ?? []) out.add(id);
  }
  const category = event.category?.trim().toLowerCase();
  if (out.size === 0 && category) {
    for (const id of TOPICS_BY_VENUE_CATEGORY.get(category) ?? []) out.add(id);
  }
  return out;
}

export interface TimeOfDayOption {
  value: string;
  label: string;
  icon: string;
}

export const TIME_OF_DAY_OPTIONS: TimeOfDayOption[] = [
  { value: 'morning', label: 'Morning (before noon)', icon: '🌅' },
  { value: 'afternoon', label: 'Afternoon (noon–5pm)', icon: '🌤️' },
  { value: 'evening', label: 'Evening (5–9pm)', icon: '🌆' },
  { value: 'late-night', label: 'Late night (9pm on)', icon: '🌙' },
  { value: 'brunch', label: 'Brunch', icon: '🥐' },
];

export const TIME_OF_DAY_VALUES = new Set(TIME_OF_DAY_OPTIONS.map((o) => o.value));

export interface UntimedInput {
  startUtc: string;
  endUtc?: string | null;
  isAllDay?: boolean | null;
  timeAnnounced?: boolean | null;
}

const MIDNIGHT_TEXT = /T00:00:00(?:\.0+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/**
 * True when the event has no clock to bucket: "Time TBA", an explicit all-day
 * flag, or a legacy row with no flag whose start (and end, if any) is written
 * as midnight. Mirrors the list's all-day rule so emails match the bar.
 */
export function isUntimedEvent(event: UntimedInput): boolean {
  if (event.timeAnnounced === false) return true;
  if (typeof event.isAllDay === 'boolean') return event.isAllDay;
  if (!MIDNIGHT_TEXT.test(event.startUtc)) return false;
  return !event.endUtc || MIDNIGHT_TEXT.test(event.endUtc);
}

/**
 * Bucket for a New York start clock in minutes after midnight. Starts from
 * midnight to 4:59am count as late night (the tail of the previous evening).
 */
export function clockBucket(minutes: number): string {
  if (minutes < 5 * 60) return 'late-night';
  if (minutes < 12 * 60) return 'morning';
  if (minutes < 17 * 60) return 'afternoon';
  if (minutes < 21 * 60) return 'evening';
  return 'late-night';
}

/** Brunch is a flavour tag, not a clock range. */
export function hasBrunchTag(event: TopicInput): boolean {
  return [event.eventType, ...(event.tags ?? [])].some((t) => t === 'brunch' || t === 'brunch-event');
}
